/**
 * Viewer preferences persisted in localStorage.
 *
 * Everything the viewer chooses (captions, volume, mute, speed, time display) lives in a
 * single JSON blob scoped per library:
 *
 *   bunny_stream_settings_<libraryId> = {"captions":"en","muted":false,"volume":0.65,...}
 *
 * Per-library, not global, because every embed shares the iframe.mediadelivery.net
 * origin — a global key would let one customer's embed read preferences a viewer set on
 * another customer's embed.
 *
 * Both the library id and the `rememberSettings` kill switch are read from the nearest
 * <media-controller>, so a call site cannot forget either one.
 */

const SETTINGS_KEY_PREFIX = 'bunny_stream_settings_';
const DEBUG_KEY = 'bunny_stream_debug';

/** Stored value meaning "the viewer turned captions off", as opposed to "no preference". */
export const CAPTIONS_OFF = 'off';

export interface PlayerSettings {
  /** Language token (e.g. "en", "pt-br") or CAPTIONS_OFF. */
  captions?: string;
  muted?: boolean;
  /** 0-1, matching HTMLMediaElement.volume. */
  volume?: number;
  speed?: number;
  timeRemaining?: boolean;
}

const LANGUAGE_PATTERN = /^[a-z0-9-]{1,35}$/;

function readRaw(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    // localStorage may be unavailable (private browsing, etc.). Note the property
    // access itself throws in Safari under "Block all cookies", so it sits inside the try.
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Storage key for the element's player, or null when preferences are unavailable —
 * no controller, no library id, or rememberSettings=false.
 */
function settingsKey(el: Element): string | null {
  const controller = el.closest('media-controller');
  if (!controller) return null;
  if (controller.getAttribute('remember-settings') === 'false') return null;

  const libraryId = controller.getAttribute('library-id');
  if (!libraryId) return null;

  return `${SETTINGS_KEY_PREFIX}${libraryId}`;
}

/** Drop anything malformed field by field, so one bad value cannot take the rest down. */
function sanitizeSettings(raw: unknown): PlayerSettings {
  if (!raw || typeof raw !== 'object') return {};

  const input = raw as Record<string, unknown>;
  const settings: PlayerSettings = {};

  if (typeof input.captions === 'string') {
    const captions = input.captions.toLowerCase();
    if (captions === CAPTIONS_OFF || LANGUAGE_PATTERN.test(captions)) {
      settings.captions = captions;
    }
  }
  if (typeof input.muted === 'boolean') {
    settings.muted = input.muted;
  }
  if (typeof input.volume === 'number' && Number.isFinite(input.volume)) {
    settings.volume = Math.min(Math.max(input.volume, 0), 1);
  }
  if (
    typeof input.speed === 'number' && Number.isFinite(input.speed) &&
    input.speed > 0
  ) {
    settings.speed = input.speed;
  }
  if (typeof input.timeRemaining === 'boolean') {
    settings.timeRemaining = input.timeRemaining;
  }

  return settings;
}

/** True when preferences may be read and written for this element's player. */
export function prefsEnabled(el: Element): boolean {
  return settingsKey(el) !== null;
}

/** Stored preferences, or an empty object when unavailable or unreadable. */
export function readSettings(el: Element): PlayerSettings {
  const key = settingsKey(el);
  if (!key) return {};

  const raw = readRaw(key);
  if (!raw) return {};

  try {
    return sanitizeSettings(JSON.parse(raw));
  } catch {
    // Corrupted blob — treat as no preferences rather than wedging the player.
    return {};
  }
}

/** Merge the given fields into the stored preferences. */
export function patchSettings(el: Element, partial: PlayerSettings): void {
  const key = settingsKey(el);
  if (!key) return;

  const next = sanitizeSettings({ ...readSettings(el), ...partial });
  writeRaw(key, JSON.stringify(next));
}

/**
 * The track matching a stored language: exact match first, then primary subtag
 * (so a stored "en" matches an "en-US" track and vice versa).
 */
export function matchTrackForLanguage<T extends { language: string }>(
  tracks: T[],
  language: string,
): T | null {
  const wanted = language.toLowerCase();

  const exact = tracks.find((track) => track.language.toLowerCase() === wanted);
  if (exact) return exact;

  const wantedBase = wanted.split('-')[0];
  return tracks.find((track) =>
    track.language.toLowerCase().split('-')[0] === wantedBase
  ) ?? null;
}

/**
 * Developer debug flag. Deliberately exempt from the kill switch and from library
 * scoping — the player only ever reads it, it is never a viewer preference.
 */
export function isDebugEnabled(): boolean {
  return readRaw(DEBUG_KEY) === 'true';
}
